/** Remove deprecated segment keys if they were copied from form data onto an API payload. */
export const omitDeprecatedSegmentWireKeys = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  const next = {...payload};
  delete next.segments;
  delete next.excludeSegments;
  delete next.selectedSegments;
  return next;
};

/** Prefer `segmentExclude` when set on form; else legacy `excludeSegments` (null/undefined fall through). */
export const resolveRawSegmentExcludeFromForm = (formData) => {
  if (
    formData?.segmentExclude !== undefined &&
    formData?.segmentExclude !== null
  ) {
    return formData.segmentExclude;
  }
  return formData?.excludeSegments;
};

/** API → form: boolean when either wire key is present; else omit (`undefined`). */
export const resolveSegmentExcludeFromApi = (src) => {
  if (src?.segmentExclude !== undefined) {
    return Boolean(src.segmentExclude);
  }
  if (src?.excludeSegments !== undefined) {
    return Boolean(src.excludeSegments);
  }
  return undefined;
};

export const resolveDisplayOrderFromApi = ({
  hasDisplayOrderFromApi,
  displayOrderFromApi
}) => {
  if (hasDisplayOrderFromApi) {
    return String(displayOrderFromApi);
  }
  return 0;
};

const formatSegmentDisplayValue = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    return item.segmentName || item.label || item.value || item.segmentId || '';
  }
  return String(item);
};

/** Format segment ids/chips as comma-separated text; optional group preservation. */
export const formatTagsCommaSeparated = (segments, options = {}) => {
  const {
    preserveGroups = false,
    groupSeparator = ' | ',
    itemSeparator = ', ',
    emptyFallback = '-'
  } = options;

  if (!Array.isArray(segments) || segments.length === 0) return emptyFallback;

  if (preserveGroups) {
    const hasNestedGroups = segments.some(Array.isArray);
    if (hasNestedGroups) {
      return (
        segments
          .map((segmentGroup) =>
            (Array.isArray(segmentGroup) ? segmentGroup : [segmentGroup])
              .map(formatSegmentDisplayValue)
              .filter(Boolean)
              .join(itemSeparator)
          )
          .filter(Boolean)
          .join(groupSeparator) || emptyFallback
      );
    }
  }

  return (
    segments
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .map(formatSegmentDisplayValue)
      .filter(Boolean)
      .join(itemSeparator) || emptyFallback
  );
};

const normalizeSelectedSegmentsToGroups = (selectedSegments) => {
  if (!Array.isArray(selectedSegments) || selectedSegments.length === 0) {
    return [];
  }
  const hasNestedGroups = selectedSegments.some(Array.isArray);
  if (hasNestedGroups) {
    return selectedSegments
      .map((group) => (Array.isArray(group) ? group : [group]))
      .filter((group) => group.length > 0);
  }
  return [selectedSegments];
};

/**
 * Review rows for included segment groups (mirrors Rewards SetupSection).
 * @returns {{ label: string, value: string }[]}
 */
export const getSegmentGroupsForReview = (
  selectedSegments,
  {emptyFallback = '-'} = {}
) => {
  const groups = normalizeSelectedSegmentsToGroups(selectedSegments);
  if (groups.length === 0) return [];

  return groups.map((group, index) => ({
    label: `Group ${index + 1}`,
    value:
      group.map(formatSegmentDisplayValue).filter(Boolean).join(', ') ||
      emptyFallback
  }));
};

/** Flatten segment ids from grouped or flat UI `segments` (deduped). */
export const normalizeActivitySegmentInput = (input) => {
  if (!Array.isArray(input)) return [];
  try {
    const isGrouped = input.some(Array.isArray);
    const flat = isGrouped ? input.flat() : input;
    return Array.from(new Set(flat.filter(Boolean)));
  } catch {
    return [];
  }
};
